interface RebuildableCell {
  rebuilding?: boolean | undefined;
}

/** Polling cadence while the server is still rebuilding cells after an update. */
export const REBUILDING_POLL_MS = 4_000;

/**
 * A cell the server is still rebuilding arrives with empty meta (no start, patient, column), so
 * the boards drop it and the appointment vanishes right after an update. Keep the last complete
 * copy of it until the rebuilt one arrives.
 */
export function carryOverRebuilding<C extends RebuildableCell>(
  next: C[],
  prev: readonly C[] | undefined,
  idOf: (cell: C) => number | undefined,
): C[] {
  if (!prev?.length || !next.some((cell) => cell.rebuilding)) return next;

  const lastComplete = new Map<number, C>();
  for (const cell of prev) {
    const id = idOf(cell);
    if (id != null && !cell.rebuilding) lastComplete.set(id, cell);
  }

  return next.map((cell) => {
    if (!cell.rebuilding) return cell;
    const id = idOf(cell);
    return (id != null ? lastComplete.get(id) : undefined) ?? cell;
  });
}

export function hasRebuildingCells(cells: readonly RebuildableCell[] | undefined): boolean {
  return cells?.some((cell) => cell.rebuilding) ?? false;
}
